import { useCallback, useEffect, useState } from "react";
import { getDailyCheckIn, submitDailyCheckIn } from "../../Services/presence";
import { CHECK_IN_COPY } from "./copy";

/**
 * The Daily Check-In flow (Journey UX §3, APS §9, `03` §7.2).
 *
 * Opened from the Home card, never on page load: the card itself renders from
 * `todays_check_in`, which the Home payload already carries. Composing costs a
 * request, so it is paid when the member asks for it.
 *
 * Order follows APS §9 — consolidated follow-ups first ("Before we talk about
 * today…"), then the 2–4 composed prompts. Every string the member reads about
 * *themselves* comes from the server: the prompt pools live in core-lambda
 * (`presence_module/check_in.py`, Journey §10) and a follow-up's wording
 * belongs to whoever requested it. This file supplies only chrome, from
 * `CHECK_IN_COPY`.
 */

/** The scale affordance for `scale_or_text` prompts (`03` §7.2).
 * Numbers only — the backend supplies no labels for the points, and naming
 * them here would be putting words in the member's mouth. */
const SCALE_POINTS = [1, 2, 3, 4, 5];

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 4l8 8M12 4l-8 8"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const Prompt = ({ prompt, value, onChange }) => {
  const scale = prompt.input === "scale_or_text";
  const selected = scale ? Number(value?.scale) : null;
  return (
    <div className="journey-checkin__prompt">
      <p className="journey-checkin__question">{prompt.text}</p>
      {scale && (
        <>
          <div
            className="journey-checkin__scale"
            role="group"
            aria-label={prompt.text}
          >
            {SCALE_POINTS.map((point) => (
              <button
                key={point}
                type="button"
                className={
                  "journey-checkin__scale-point" +
                  (selected === point
                    ? " journey-checkin__scale-point--on"
                    : "")
                }
                aria-pressed={selected === point}
                onClick={() =>
                  onChange({
                    ...(value || {}),
                    scale: selected === point ? null : point,
                  })
                }
              >
                {point}
              </button>
            ))}
          </div>
          <p className="journey-checkin__hint">{CHECK_IN_COPY.scaleHint}</p>
        </>
      )}
      <textarea
        className="journey-checkin__input"
        rows={2}
        value={value?.text || ""}
        aria-label={prompt.text}
        onChange={(event) =>
          onChange({ ...(value || {}), text: event.target.value })
        }
      />
    </div>
  );
};

/** Answers → the `responses` map the API stores, dropping the empty ones so an
 * untouched prompt is recorded as unanswered rather than as an empty string. */
const toResponses = (prompts, answers) => {
  const responses = {};
  prompts.forEach((prompt) => {
    const answer = answers[prompt.key];
    if (!answer) return;
    const text = (answer.text || "").trim();
    const hasScale = typeof answer.scale === "number";
    if (!text && !hasScale) return;
    if (hasScale && text) responses[prompt.key] = { scale: answer.scale, text };
    else if (hasScale) responses[prompt.key] = answer.scale;
    else responses[prompt.key] = text;
  });
  return responses;
};

/** The Reflection-group answers, joined — this is the line the Home card shows
 * back to the member, so it is sent as `reflection` as well as inside
 * `responses` (the model carries both: the full record and the surfaced one). */
const toReflection = (prompts, answers) =>
  prompts
    .filter((prompt) => prompt.group === "reflection")
    .map((prompt) => (answers[prompt.key]?.text || "").trim())
    .filter(Boolean)
    .join(" ") || null;

const CheckInFlow = ({ onClose, onCompleted }) => {
  const [checkIn, setCheckIn] = useState(null);
  const [answers, setAnswers] = useState({});
  const [followUps, setFollowUps] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getDailyCheckIn();
        if (!cancelled) setCheckIn(payload || null);
      } catch (_) {
        if (!cancelled) setError(CHECK_IN_COPY.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = useCallback(async () => {
    if (!checkIn || saving) return;
    setSaving(true);
    setError("");
    try {
      const prompts = checkIn.prompts || [];
      const result = await submitDailyCheckIn({
        checkInId: checkIn.check_in_id,
        responses: toResponses(prompts, answers),
        reflection: toReflection(prompts, answers),
        followUpResponses: Object.fromEntries(
          Object.entries(followUps)
            .map(([id, text]) => [id, (text || "").trim()])
            .filter(([, text]) => text)
        ),
      });
      onCompleted(result);
    } catch (_) {
      setError(CHECK_IN_COPY.submitError);
      setSaving(false);
    }
  }, [checkIn, answers, followUps, saving, onCompleted]);

  const prompts = checkIn?.prompts || [];
  const pending = checkIn?.pending_follow_ups || [];

  return (
    <div className="journey-checkin" role="dialog" aria-modal="true">
      <div className="journey-checkin__backdrop" onClick={onClose} />
      <div className="journey-checkin__sheet">
        <div className="journey-checkin__head">
          <h2 className="journey-card__title">{CHECK_IN_COPY.heading}</h2>
          <button
            type="button"
            className="journey-checkin__close"
            aria-label={CHECK_IN_COPY.close}
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="journey-checkin__body">
          {loading && (
            <p className="journey-card__meta">{CHECK_IN_COPY.loading}</p>
          )}
          {!loading && error && !checkIn && (
            <p className="journey-card__meta">{error}</p>
          )}

          {!loading && checkIn && (
            <>
              {pending.length > 0 && (
                <div className="journey-checkin__group">
                  <p className="journey-checkin__group-label">
                    {CHECK_IN_COPY.followUpsHeading}
                  </p>
                  {pending.map((item) => (
                    <div
                      className="journey-checkin__prompt"
                      key={item.presence_id}
                    >
                      {/* Rendered only when the presence carries its own
                          wording; a copy-key-only follow-up has no string this
                          client can resolve, and inventing one is not an
                          option. */}
                      {item.prompt && (
                        <p className="journey-checkin__question">
                          {item.prompt}
                        </p>
                      )}
                      <textarea
                        className="journey-checkin__input"
                        rows={2}
                        aria-label={item.prompt || item.presence_id}
                        value={followUps[item.presence_id] || ""}
                        onChange={(event) =>
                          setFollowUps((current) => ({
                            ...current,
                            [item.presence_id]: event.target.value,
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="journey-checkin__group">
                {prompts.map((prompt) => (
                  <Prompt
                    key={prompt.key}
                    prompt={prompt}
                    value={answers[prompt.key]}
                    onChange={(next) =>
                      setAnswers((current) => ({
                        ...current,
                        [prompt.key]: next,
                      }))
                    }
                  />
                ))}
              </div>

              {error && <p className="journey-card__meta">{error}</p>}
            </>
          )}
        </div>

        {!loading && checkIn && (
          <button
            type="button"
            className="journey-button journey-checkin__submit"
            onClick={submit}
            disabled={saving}
          >
            {saving ? CHECK_IN_COPY.submitting : CHECK_IN_COPY.submit}
          </button>
        )}
      </div>
    </div>
  );
};

export default CheckInFlow;
